import React, { useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { DateRange } from '@mui/x-date-pickers-pro';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGetSuggestions } from '../../apiHooks/queries/suggestions/useGetSuggestions';
import { SuggestionInterface } from '../../interfaces/suggestion.interface';
import { TrackingSourceInterface } from '../../interfaces/trackingSource.interface';
import { formatDate } from '../../constants/hookConstants';
import CustomDateRangePicker from '../../partials/customDateRangePicker/customDateRangePicker';
import { Colors } from '../../constants/constants';
import SecondaryButton from '../buttons/secondaryButton';
import TrackingSourceSelect from '../dropdownSelects/trackingSourceSelect';
import PlayStopAdCell from '../../partials/playStopAdCell';

interface SuggestionsModalProps {
  closeModal: () => void;
}

const AD_OBJECT_TYPE = 1;

const SuggestionsModal: React.FC<SuggestionsModalProps> = ({ closeModal }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [dateRange, setDateRange] = useState<DateRange<Dayjs>>([
    dayjs().subtract(5, 'day'),
    dayjs(),
  ]);
  const [isCalendarOpen, setIsCalendarOpen] = useState<boolean>(false);
  const [selectedTrackingSource, setSelectedTrackingSource] = useState<TrackingSourceInterface | null>(null);

  const { data, isLoading } = useGetSuggestions(
    formatDate(dateRange[0]),
    formatDate(dateRange[1]),
    selectedTrackingSource?.id ?? null,
  );

  const suggestions: SuggestionInterface[] = (data?.data ?? []).filter((suggestion) => !suggestion.dismissedAt);

  const formatInterval = (start: string, end: string) => {
    if (!start || !end) return '';
    return `${dayjs(start).format('MMM D, YYYY')} - ${dayjs(end).format('MMM D, YYYY')}`;
  };

  const goToAdDetail = (objectCode: string | number) => {
    navigate(`/ad-page/${objectCode}`);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onMouseDown={closeModal}
    >
      <div
        className="flex max-h-[80vh] w-[600px] max-w-[90vw] flex-col rounded-lg bg-white p-6 shadow-lg"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between border-b border-b-[#E0E0E0] pb-4 p-1">
          <h2 className="text-base font-medium text-primary">{t('suggestions')}</h2>
          <button
            type="button"
            onClick={closeModal}
            className="text-3xl leading-none text-gray-3 hover:text-primary"
            aria-label="close"
          >
            &times;
          </button>
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-3 border-b border-b-[#E0E0E0] pb-4">
          <SecondaryButton
            type="button"
            style={{
              paddingInline: '1rem',
              height: '40px',
              paddingBlock: 0,
              backgroundColor: 'white',
              border: `1px solid ${isCalendarOpen ? Colors.primary : '#eaeaea'}`,
            }}
            onMouseDown={() => setIsCalendarOpen(true)}
          >
            <CustomDateRangePicker
              filterRange={dateRange}
              setFilterRange={setDateRange}
              isCalendarOpen={isCalendarOpen}
              setIsCalendarOpen={setIsCalendarOpen}
              iconButton
              showFilterDates
              anchorToTrigger
            />
          </SecondaryButton>
          <div className="w-[200px]">
            <TrackingSourceSelect
              size="small"
              fullWidth
              hideDirect
              label={t('selectTrackingSource') as string}
              placeholder={t('selectTrackingSource') as string}
              data={selectedTrackingSource ?? undefined}
              setSelectedTrackingSource={setSelectedTrackingSource}
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto pr-1">
          {isLoading && (
            <p className="py-8 text-center text-sm text-gray-3">{t('loading')}</p>
          )}

          {!isLoading && suggestions.length === 0 && (
            <p className="py-8 text-center text-sm text-gray-3">{t('noSuggestions')}</p>
          )}

          {!isLoading && suggestions.length > 0 && (
            <ul className="flex flex-col gap-3">
              {suggestions.map((suggestion) => (
                <li
                  key={suggestion.id}
                  className="flex items-center justify-between gap-4 rounded-lg border border-[#E0E0E0] p-4"
                >
                  <div className="flex flex-col gap-1">
                    <span className="flex items-center gap-1.5 text-sm font-medium text-primary">
                      <span className="font-semibold">{`${t('suggestedAction')}: `}</span>
                      {suggestion.suggestedAction}
                      {suggestion.note && (
                        <span
                          className="group relative inline-flex cursor-help text-gray-3"
                          tabIndex={0}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="16" x2="12" y2="12" />
                            <line x1="12" y1="8" x2="12.01" y2="8" />
                          </svg>
                          <span className="pointer-events-none absolute left-1/2 top-full z-10 mt-1 hidden w-max max-w-[220px] -translate-x-1/2 whitespace-normal rounded-md bg-gray-800 px-2 py-1 text-xs font-normal text-white shadow-lg group-hover:block group-focus:block">
                            {suggestion.note}
                          </span>
                        </span>
                      )}
                    </span>
                    {suggestion.reason && (
                      <span className="text-xs text-gray-3">
                        <span className="font-semibold">{`${t('reason')}: `}</span>
                        {suggestion.reason}
                      </span>
                    )}
                    {(suggestion.objectType === AD_OBJECT_TYPE
                      || (suggestion.objectCode != null && suggestion.objectCode !== '')
                      || suggestion.name) && (
                      <span className="flex items-center gap-1.5 text-xs text-gray-3">
                        {(suggestion.objectCode != null && suggestion.objectCode !== '') && (
                          <span>{suggestion.objectCode}</span>
                        )}
                        {suggestion.objectType === AD_OBJECT_TYPE && (
                          <button
                            type="button"
                            onClick={() => goToAdDetail(suggestion.objectCode ?? suggestion.objectId)}
                            className="text-primary hover:opacity-80"
                            aria-label={t('viewAd') as string}
                            title={t('viewAd') as string}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                              <polyline points="15 3 21 3 21 9" />
                              <line x1="10" y1="14" x2="21" y2="3" />
                            </svg>
                          </button>
                        )}
                        {suggestion.name && (
                          <span className="font-medium">{`- ${suggestion.name}`}</span>
                        )}
                      </span>
                    )}
                    {(suggestion.intervalStart || suggestion.intervalEnd) && (
                      <span className="text-xs text-gray-3">
                        {`${t('period')}: ${formatInterval(suggestion.intervalStart, suggestion.intervalEnd)}`}
                      </span>
                    )}
                  </div>
                  {suggestion.objectType === AD_OBJECT_TYPE && (
                    <div className="flex items-center gap-2">
                      <PlayStopAdCell
                        params
                        rowObject={{
                          adId: suggestion.objectCode ?? null,
                          trackingSourceId: suggestion.trackingSourceId,
                        }}
                      />
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default SuggestionsModal;
